// Astronomy core: solar/lunar position, twilight times, planet visibility hints,
// and the annual meteor shower calendar.
//
// All math is "good enough for visualization" — within ~1° for sun, ~2° for moon,
// and roughly correct for planet visibility windows.

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

// ── Time helpers ────────────────────────────────────────────────────────────

export function julianDay(date: Date): number {
  return date.getTime() / 86400000 + 2440587.5;
}

export function julianCenturies(jd: number): number {
  return (jd - 2451545.0) / 36525;
}

// ── Solar position (NOAA approximation) ─────────────────────────────────────

export interface SunPosition {
  altitudeDeg: number;
  azimuthDeg: number; // 0 = North, 90 = East, 180 = South, 270 = West
  declinationDeg: number;
  hourAngleDeg: number;
}

export function solarPosition(date: Date, lat: number, lon: number): SunPosition {
  const jd = julianDay(date);
  const t = julianCenturies(jd);

  // Geometric mean longitude (deg)
  const L0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  // Geometric mean anomaly (deg)
  const M = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  // Eccentricity of Earth's orbit
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  // Sun's equation of center
  const C =
    Math.sin(M * DEG) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * M * DEG) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * M * DEG) * 0.000289;
  // True longitude / true anomaly
  const trueLong = L0 + C;
  // Apparent longitude (correction for nutation)
  const omega = 125.04 - 1934.136 * t;
  const lambda = trueLong - 0.00569 - 0.00478 * Math.sin(omega * DEG);
  // Obliquity of the ecliptic
  const obliq =
    23 +
    (26 +
      (21.448 -
        t * (46.815 + t * (0.00059 - t * 0.001813))) /
        60) /
      60;
  const obliqCorr = obliq + 0.00256 * Math.cos(omega * DEG);
  // Declination
  const decl = Math.asin(Math.sin(obliqCorr * DEG) * Math.sin(lambda * DEG)) * RAD;
  // Equation of time (minutes)
  const y = Math.tan((obliqCorr / 2) * DEG) ** 2;
  const eqTime =
    4 *
    RAD *
    (y * Math.sin(2 * L0 * DEG) -
      2 * e * Math.sin(M * DEG) +
      4 * e * y * Math.sin(M * DEG) * Math.cos(2 * L0 * DEG) -
      0.5 * y * y * Math.sin(4 * L0 * DEG) -
      1.25 * e * e * Math.sin(2 * M * DEG));
  // Local solar time
  const utcMinutes = date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
  const trueSolarTime = (utcMinutes + eqTime + 4 * lon) % 1440;
  // Hour angle (degrees, 0 at noon)
  let hourAngle = trueSolarTime / 4 - 180;
  if (hourAngle < -180) hourAngle += 360;
  // Altitude
  const zenith =
    Math.acos(
      Math.sin(lat * DEG) * Math.sin(decl * DEG) +
        Math.cos(lat * DEG) * Math.cos(decl * DEG) * Math.cos(hourAngle * DEG),
    ) * RAD;
  const alt = 90 - zenith;
  // Azimuth (0=N, 90=E)
  let az;
  const denom = Math.cos(lat * DEG) * Math.sin(zenith * DEG);
  if (Math.abs(denom) < 1e-10) {
    az = lat > 0 ? 180 : 0;
  } else {
    const cosAz =
      (Math.sin(decl * DEG) - Math.sin(lat * DEG) * Math.cos(zenith * DEG)) / denom;
    const clamped = Math.max(-1, Math.min(1, cosAz));
    az = Math.acos(clamped) * RAD;
    if (hourAngle > 0) az = 360 - az;
  }
  return {
    altitudeDeg: alt,
    azimuthDeg: az,
    declinationDeg: decl,
    hourAngleDeg: hourAngle,
  };
}

// ── Twilight times (find when solar altitude crosses target) ────────────────
//
// Returns timestamps (seconds) for when sun reaches given altitude crossing.
// Brute-force search across the day; precise to ~30s.

export interface TwilightTimes {
  // morning crossings
  astroDawn: number | null;
  nauticalDawn: number | null;
  civilDawn: number | null;
  sunrise: number | null;
  // evening crossings
  sunset: number | null;
  civilDusk: number | null;
  nauticalDusk: number | null;
  astroDusk: number | null;
  // sky condition flags
  alwaysDay: boolean;
  alwaysNight: boolean;
}

export function twilightTimes(date: Date, lat: number, lon: number): TwilightTimes {
  // Sample every 5 minutes across the local day (UTC midnight ± 12h around solar noon).
  const startMs =
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0) -
    Math.round(lon / 15) * 3600 * 1000;
  const stepMs = 5 * 60 * 1000;
  const samples: { ts: number; alt: number }[] = [];
  for (let i = 0; i <= 24 * 12; i++) {
    const t = new Date(startMs + i * stepMs);
    samples.push({ ts: t.getTime() / 1000, alt: solarPosition(t, lat, lon).altitudeDeg });
  }

  // Find two zero-crossings (or single, or none) for each target altitude.
  function findCrossings(
    target: number,
  ): { rising: number | null; setting: number | null; alwaysAbove: boolean; alwaysBelow: boolean } {
    let rising: number | null = null;
    let setting: number | null = null;
    let allAbove = true;
    let allBelow = true;
    for (let i = 1; i < samples.length; i++) {
      const a = samples[i - 1];
      const b = samples[i];
      if (a.alt < target || b.alt < target) allAbove = false;
      if (a.alt > target || b.alt > target) allBelow = false;
      if ((a.alt - target) * (b.alt - target) <= 0 && a.alt !== b.alt) {
        // Linear interpolation
        const frac = (target - a.alt) / (b.alt - a.alt);
        const ts = a.ts + (b.ts - a.ts) * frac;
        if (a.alt < b.alt && rising == null) rising = ts;
        else if (a.alt > b.alt && setting == null) setting = ts;
      }
    }
    return { rising, setting, alwaysAbove: allAbove, alwaysBelow: allBelow };
  }

  const sun = findCrossings(-0.833); // accounts for atmospheric refraction
  const civil = findCrossings(-6);
  const nautical = findCrossings(-12);
  const astro = findCrossings(-18);

  return {
    astroDawn: astro.rising,
    nauticalDawn: nautical.rising,
    civilDawn: civil.rising,
    sunrise: sun.rising,
    sunset: sun.setting,
    civilDusk: civil.setting,
    nauticalDusk: nautical.setting,
    astroDusk: astro.setting,
    alwaysDay: sun.alwaysAbove,
    alwaysNight: sun.alwaysBelow,
  };
}

// ── Lunar position (simplified — Meeus-lite, accuracy ~1°) ───────────────────

export interface MoonPosition {
  altitudeDeg: number;
  azimuthDeg: number;
  illumination: number; // 0..1
  phaseAngleDeg: number; // 0=new, 180=full
  ageDays: number; // 0..29.53
  distanceKm: number;
}

export function lunarPosition(date: Date, lat: number, lon: number): MoonPosition {
  const jd = julianDay(date);
  const d = jd - 2451545.0;

  // Mean elements
  const Lm = (218.316 + 13.176396 * d) % 360;
  const Mm = (134.963 + 13.064993 * d) % 360;
  const Mf = (93.272 + 13.22935 * d) % 360;
  const Ms = (357.5291 + 0.98560028 * d) % 360;
  const Dm = (297.8501 + 12.190749 * d) % 360;

  // Ecliptic longitude (largest series terms only)
  const lonEcl =
    Lm +
    6.289 * Math.sin(Mm * DEG) -
    1.274 * Math.sin((2 * Dm - Mm) * DEG) +
    0.658 * Math.sin(2 * Dm * DEG) -
    0.186 * Math.sin(Ms * DEG) -
    0.059 * Math.sin((2 * Mm - 2 * Dm) * DEG) -
    0.057 * Math.sin((Mm - 2 * Dm + Ms) * DEG);
  // Latitude
  const latEcl = 5.128 * Math.sin(Mf * DEG) + 0.281 * Math.sin((Mm + Mf) * DEG);
  // Distance in km
  const dist =
    385000.56 +
    -20905 * Math.cos(Mm * DEG) -
    3699 * Math.cos((2 * Dm - Mm) * DEG) -
    2956 * Math.cos(2 * Dm * DEG);

  // Convert to equatorial
  const epsilon = 23.4397 * DEG;
  const sinL = Math.sin(lonEcl * DEG);
  const cosL = Math.cos(lonEcl * DEG);
  const sinB = Math.sin(latEcl * DEG);
  const cosB = Math.cos(latEcl * DEG);
  const ra = Math.atan2(
    sinL * Math.cos(epsilon) - (sinB / cosB) * Math.sin(epsilon),
    cosL,
  ) * RAD;
  const dec =
    Math.asin(sinB * Math.cos(epsilon) + cosB * Math.sin(epsilon) * sinL) * RAD;

  // Local sidereal time
  const utc = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const gst = (18.697374558 + 24.06570982441908 * d) % 24;
  const lst = (gst * 15 + lon) % 360;
  const ha = (lst - ra + 720) % 360;

  // Altitude / azimuth
  const sinAlt =
    Math.sin(lat * DEG) * Math.sin(dec * DEG) +
    Math.cos(lat * DEG) * Math.cos(dec * DEG) * Math.cos(ha * DEG);
  const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * RAD;
  const cosAz =
    (Math.sin(dec * DEG) - Math.sin(alt * DEG) * Math.sin(lat * DEG)) /
    (Math.cos(alt * DEG) * Math.cos(lat * DEG));
  let az = Math.acos(Math.max(-1, Math.min(1, cosAz))) * RAD;
  if (Math.sin(ha * DEG) > 0) az = 360 - az;

  // Phase angle and illumination
  const phaseAngle = (Lm - (280.46 + 0.9856474 * d)) % 360;
  const phaseRad = phaseAngle * DEG;
  const illumination = (1 - Math.cos(phaseRad)) / 2;

  // Age in days (since new moon — synodic period 29.530589)
  let age = ((phaseAngle + 360) % 360) / 360 * 29.530589;
  if (age > 29.530589) age -= 29.530589;

  return {
    altitudeDeg: alt,
    azimuthDeg: az,
    illumination,
    phaseAngleDeg: ((phaseAngle + 360) % 360),
    ageDays: age,
    distanceKm: dist,
  };
}

export const MOON_PHASES = [
  { name: "New Moon", emoji: "🌑", min: 0, max: 0.0625 },
  { name: "Waxing Crescent", emoji: "🌒", min: 0.0625, max: 0.1875 },
  { name: "First Quarter", emoji: "🌓", min: 0.1875, max: 0.3125 },
  { name: "Waxing Gibbous", emoji: "🌔", min: 0.3125, max: 0.4375 },
  { name: "Full Moon", emoji: "🌕", min: 0.4375, max: 0.5625 },
  { name: "Waning Gibbous", emoji: "🌖", min: 0.5625, max: 0.6875 },
  { name: "Last Quarter", emoji: "🌗", min: 0.6875, max: 0.8125 },
  { name: "Waning Crescent", emoji: "🌘", min: 0.8125, max: 0.9375 },
  { name: "New Moon", emoji: "🌑", min: 0.9375, max: 1.001 },
];

export function moonPhaseFromFraction(fraction: number): { name: string; emoji: string } {
  for (const p of MOON_PHASES) {
    if (fraction >= p.min && fraction < p.max) return { name: p.name, emoji: p.emoji };
  }
  return MOON_PHASES[0];
}

// ── Meteor shower calendar ──────────────────────────────────────────────────

export interface MeteorShower {
  name: string;
  parent: string;
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
  peakMonth: number;
  peakDay: number;
  zhr: number; // expected zenith hourly rate at peak
  velocityKms: number;
  notes: string;
}

export const METEOR_SHOWERS: MeteorShower[] = [
  {
    name: "Quadrantids",
    parent: "Asteroid (196256) 2003 EH1",
    startMonth: 12,
    startDay: 28,
    endMonth: 1,
    endDay: 12,
    peakMonth: 1,
    peakDay: 4,
    zhr: 110,
    velocityKms: 41,
    notes: "Sharp peak lasting only ~6 hours; observers in northern hemisphere favored.",
  },
  {
    name: "Lyrids",
    parent: "Comet C/1861 G1 Thatcher",
    startMonth: 4,
    startDay: 16,
    endMonth: 4,
    endDay: 25,
    peakMonth: 4,
    peakDay: 22,
    zhr: 18,
    velocityKms: 49,
    notes: "Reliable, modest shower. Occasional outbursts.",
  },
  {
    name: "Eta Aquariids",
    parent: "Comet 1P/Halley",
    startMonth: 4,
    startDay: 19,
    endMonth: 5,
    endDay: 28,
    peakMonth: 5,
    peakDay: 6,
    zhr: 50,
    velocityKms: 66,
    notes: "Best from southern hemisphere; pre-dawn viewing.",
  },
  {
    name: "Southern δ Aquariids",
    parent: "Comet 96P/Machholz",
    startMonth: 7,
    startDay: 12,
    endMonth: 8,
    endDay: 23,
    peakMonth: 7,
    peakDay: 30,
    zhr: 25,
    velocityKms: 41,
    notes: "Broad maximum; favors southern latitudes.",
  },
  {
    name: "Perseids",
    parent: "Comet 109P/Swift-Tuttle",
    startMonth: 7,
    startDay: 17,
    endMonth: 8,
    endDay: 24,
    peakMonth: 8,
    peakDay: 12,
    zhr: 100,
    velocityKms: 59,
    notes: "Most popular shower of the year. Long, bright trails.",
  },
  {
    name: "Draconids",
    parent: "Comet 21P/Giacobini-Zinner",
    startMonth: 10,
    startDay: 6,
    endMonth: 10,
    endDay: 10,
    peakMonth: 10,
    peakDay: 8,
    zhr: 10,
    velocityKms: 20,
    notes: "Typically modest, but produces occasional storms (1933, 1946, 2011).",
  },
  {
    name: "Orionids",
    parent: "Comet 1P/Halley",
    startMonth: 10,
    startDay: 2,
    endMonth: 11,
    endDay: 7,
    peakMonth: 10,
    peakDay: 21,
    zhr: 20,
    velocityKms: 66,
    notes: "Fast meteors, often leave trains. Second Halley shower.",
  },
  {
    name: "Northern Taurids",
    parent: "Comet 2P/Encke",
    startMonth: 10,
    startDay: 20,
    endMonth: 12,
    endDay: 10,
    peakMonth: 11,
    peakDay: 12,
    zhr: 5,
    velocityKms: 29,
    notes: "Slow, bright fireballs more common than typical showers.",
  },
  {
    name: "Leonids",
    parent: "Comet 55P/Tempel-Tuttle",
    startMonth: 11,
    startDay: 6,
    endMonth: 11,
    endDay: 30,
    peakMonth: 11,
    peakDay: 17,
    zhr: 15,
    velocityKms: 71,
    notes: "Famous for periodic storms (every ~33 years).",
  },
  {
    name: "Geminids",
    parent: "Asteroid 3200 Phaethon",
    startMonth: 12,
    startDay: 4,
    endMonth: 12,
    endDay: 17,
    peakMonth: 12,
    peakDay: 14,
    zhr: 150,
    velocityKms: 35,
    notes: "Best shower of the year. Bright, multicolored, slow meteors.",
  },
  {
    name: "Ursids",
    parent: "Comet 8P/Tuttle",
    startMonth: 12,
    startDay: 17,
    endMonth: 12,
    endDay: 26,
    peakMonth: 12,
    peakDay: 22,
    zhr: 10,
    velocityKms: 33,
    notes: "Quiet shower near solstice; northern hemisphere only.",
  },
];

function inRange(
  m: number,
  d: number,
  startM: number,
  startD: number,
  endM: number,
  endD: number,
): boolean {
  // Handle ranges that wrap year-end (e.g., Dec 28 - Jan 12)
  const cur = m * 100 + d;
  const start = startM * 100 + startD;
  const end = endM * 100 + endD;
  if (start <= end) return cur >= start && cur <= end;
  return cur >= start || cur <= end;
}

export interface ShowerStatus extends MeteorShower {
  active: boolean;
  daysToPeak: number; // negative if peak passed; in days
  peakDate: Date;
}

export function getShowerStatuses(date: Date): ShowerStatus[] {
  const m = date.getUTCMonth() + 1;
  const d = date.getUTCDate();
  const year = date.getUTCFullYear();
  return METEOR_SHOWERS.map((s) => {
    const active = inRange(m, d, s.startMonth, s.startDay, s.endMonth, s.endDay);
    let peakYear = year;
    if (s.peakMonth < m || (s.peakMonth === m && s.peakDay < d)) peakYear = year + 1;
    if (s.startMonth > s.endMonth) {
      // wrap shower; pick whichever peak (current year or next) is closest
      const candA = new Date(Date.UTC(year, s.peakMonth - 1, s.peakDay));
      const candB = new Date(Date.UTC(year + 1, s.peakMonth - 1, s.peakDay));
      const candC = new Date(Date.UTC(year - 1, s.peakMonth - 1, s.peakDay));
      const cands = [candA, candB, candC];
      const futureOrRecent = cands
        .map((c) => ({ c, delta: (c.getTime() - date.getTime()) / 86400000 }))
        .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
      peakYear = futureOrRecent[0].c.getUTCFullYear();
    }
    const peakDate = new Date(Date.UTC(peakYear, s.peakMonth - 1, s.peakDay));
    const daysToPeak = (peakDate.getTime() - date.getTime()) / 86400000;
    return { ...s, active, daysToPeak, peakDate };
  }).sort((a, b) => {
    // Active first, then by absolute days-to-peak
    if (a.active && !b.active) return -1;
    if (!a.active && b.active) return 1;
    return Math.abs(a.daysToPeak) - Math.abs(b.daysToPeak);
  });
}

// ── Visible planets (simplified) ────────────────────────────────────────────
//
// Computes approximate ecliptic longitude of inner/outer planets and reports
// whether each is currently above the horizon at the observer's location.
// Accuracy: a few degrees in position; visibility windows are approximate.

interface OrbitalElements {
  // J2000 mean elements; rates are per Julian century (T)
  a: number; // AU
  e: number;
  i: number; // deg
  L: number; // mean longitude
  longPeri: number; // longitude of perihelion
  longNode: number; // longitude of ascending node
  // rates (per century)
  da?: number; dE?: number; di?: number; dL: number; dPeri?: number; dNode?: number;
}

const ELEMENTS: Record<string, OrbitalElements> = {
  Mercury: {
    a: 0.38709927, e: 0.20563593, i: 7.00497902,
    L: 252.25032350, longPeri: 77.45779628, longNode: 48.33076593,
    dL: 149472.67411175, dPeri: 0.16047689, dNode: -0.12534081, dE: 0.00001906, di: -0.00594749,
  },
  Venus: {
    a: 0.72333566, e: 0.00677672, i: 3.39467605,
    L: 181.97909950, longPeri: 131.60246718, longNode: 76.67984255,
    dL: 58517.81538729, dPeri: 0.00268329, dNode: -0.27769418, dE: -0.00004107, di: -0.00078890,
  },
  Earth: {
    a: 1.00000261, e: 0.01671123, i: -0.00001531,
    L: 100.46457166, longPeri: 102.93768193, longNode: 0,
    dL: 35999.37244981, dPeri: 0.32327364, dNode: 0, dE: -0.00004392, di: -0.01294668,
  },
  Mars: {
    a: 1.52371034, e: 0.09339410, i: 1.84969142,
    L: -4.55343205, longPeri: -23.94362959, longNode: 49.55953891,
    dL: 19140.30268499, dPeri: 0.44441088, dNode: -0.29257343, dE: 0.00007882, di: -0.00813131,
  },
  Jupiter: {
    a: 5.20288700, e: 0.04838624, i: 1.30439695,
    L: 34.39644051, longPeri: 14.72847983, longNode: 100.47390909,
    dL: 3034.74612775, dPeri: 0.21252668, dNode: 0.20469106, dE: -0.00013253, di: -0.00183714,
  },
  Saturn: {
    a: 9.53667594, e: 0.05386179, i: 2.48599187,
    L: 49.95424423, longPeri: 92.59887831, longNode: 113.66242448,
    dL: 1222.49362201, dPeri: -0.41897216, dNode: -0.28867794, dE: -0.00050991, di: 0.00193609,
  },
};

function computeHelioCoords(
  el: OrbitalElements,
  T: number,
): { x: number; y: number; z: number } {
  const a = el.a + (el.da ?? 0) * T;
  const e = el.e + (el.dE ?? 0) * T;
  const i = (el.i + (el.di ?? 0) * T) * DEG;
  const L = (el.L + el.dL * T) % 360;
  const longPeri = el.longPeri + (el.dPeri ?? 0) * T;
  const longNode = el.longNode + (el.dNode ?? 0) * T;
  const M = ((L - longPeri) * DEG) % (2 * Math.PI);
  // Solve Kepler iteratively
  let E = M;
  for (let k = 0; k < 6; k++) {
    E = E - (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
  }
  // Heliocentric coords in orbit plane
  const xp = a * (Math.cos(E) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(E);
  const w = (longPeri - longNode) * DEG;
  const Om = longNode * DEG;
  const cosw = Math.cos(w), sinw = Math.sin(w);
  const cosOm = Math.cos(Om), sinOm = Math.sin(Om);
  const cosi = Math.cos(i), sini = Math.sin(i);
  const x =
    (cosw * cosOm - sinw * sinOm * cosi) * xp +
    (-sinw * cosOm - cosw * sinOm * cosi) * yp;
  const y =
    (cosw * sinOm + sinw * cosOm * cosi) * xp +
    (-sinw * sinOm + cosw * cosOm * cosi) * yp;
  const z = sinw * sini * xp + cosw * sini * yp;
  return { x, y, z };
}

export interface PlanetPosition {
  name: string;
  altitudeDeg: number;
  azimuthDeg: number;
  illuminatedFraction: number;
  magnitudeApprox: number;
  eclipticLonDeg: number; // for relative ordering
  visibleNow: boolean;
}

const PLANET_NAMES = ["Mercury", "Venus", "Mars", "Jupiter", "Saturn"] as const;

export function planetPositions(date: Date, lat: number, lon: number): PlanetPosition[] {
  const jd = julianDay(date);
  const T = julianCenturies(jd);
  const earth = computeHelioCoords(ELEMENTS.Earth, T);

  const utc = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const d = jd - 2451545.0;
  const gst = (18.697374558 + 24.06570982441908 * d) % 24;
  const lst = (gst * 15 + lon) % 360;

  return PLANET_NAMES.map((name) => {
    const el = ELEMENTS[name];
    const p = computeHelioCoords(el, T);
    // Geocentric coords (Earth-centered)
    const gx = p.x - earth.x;
    const gy = p.y - earth.y;
    const gz = p.z - earth.z;
    // Convert ecliptic to equatorial
    const epsilon = 23.4397 * DEG;
    const xe = gx;
    const ye = gy * Math.cos(epsilon) - gz * Math.sin(epsilon);
    const ze = gy * Math.sin(epsilon) + gz * Math.cos(epsilon);
    const ra = (Math.atan2(ye, xe) * RAD + 360) % 360;
    const dec = Math.asin(ze / Math.sqrt(xe * xe + ye * ye + ze * ze)) * RAD;
    const ha = (lst - ra + 720) % 360;
    const sinAlt =
      Math.sin(lat * DEG) * Math.sin(dec * DEG) +
      Math.cos(lat * DEG) * Math.cos(dec * DEG) * Math.cos(ha * DEG);
    const alt = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * RAD;
    const cosAz =
      (Math.sin(dec * DEG) - Math.sin(alt * DEG) * Math.sin(lat * DEG)) /
      (Math.cos(alt * DEG) * Math.cos(lat * DEG));
    let az = Math.acos(Math.max(-1, Math.min(1, cosAz))) * RAD;
    if (Math.sin(ha * DEG) > 0) az = 360 - az;

    // Phase angle: earth-planet-sun
    const rPlanetSun = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
    const rEarthPlanet = Math.sqrt(gx * gx + gy * gy + gz * gz);
    const rEarthSun = Math.sqrt(earth.x * earth.x + earth.y * earth.y + earth.z * earth.z);
    const cosPhase =
      (rPlanetSun * rPlanetSun + rEarthPlanet * rEarthPlanet - rEarthSun * rEarthSun) /
      (2 * rPlanetSun * rEarthPlanet);
    const phase = Math.acos(Math.max(-1, Math.min(1, cosPhase)));
    const illumination = (1 + Math.cos(phase)) / 2;
    // Approximate visual magnitude (rough)
    const baseMag: Record<string, number> = {
      Mercury: -0.6, Venus: -4.4, Mars: -2.0, Jupiter: -9.4, Saturn: -8.9,
    };
    const mag =
      baseMag[name] +
      5 * Math.log10(rPlanetSun * rEarthPlanet) -
      2.5 * Math.log10(illumination + 0.0001);

    return {
      name,
      altitudeDeg: alt,
      azimuthDeg: az,
      illuminatedFraction: illumination,
      magnitudeApprox: Math.round(mag * 10) / 10,
      eclipticLonDeg: ra,
      visibleNow: alt > 0,
    };
  });
}

// ── Stargazing rating per night ─────────────────────────────────────────────

export interface NightRating {
  date: Date;
  cloudPct: number;
  moonIllumination: number; // 0..1
  moonPhaseEmoji: string;
  score: number; // 0..10
  rating: "Excellent" | "Good" | "Fair" | "Poor";
}

export function rateNight(input: {
  date: Date;
  cloudPct: number;
  moonPhaseFraction: number;
}): NightRating {
  const cloudScore = 10 - input.cloudPct / 10;
  const illumination = 1 - Math.abs(input.moonPhaseFraction - 0.5) * 2;
  const moonScore = 10 - illumination * 5;
  const raw = cloudScore * 0.65 + moonScore * 0.35;
  const score = Math.round(Math.max(0, Math.min(10, raw)) * 10) / 10;
  const rating: NightRating["rating"] =
    score >= 8 ? "Excellent" : score >= 6 ? "Good" : score >= 4 ? "Fair" : "Poor";
  const phase = moonPhaseFromFraction(input.moonPhaseFraction);
  return {
    date: input.date,
    cloudPct: input.cloudPct,
    moonIllumination: illumination,
    moonPhaseEmoji: phase.emoji,
    score,
    rating,
  };
}
